import React, { ChangeEvent, DragEvent, useRef, useState } from "react";
import { Modal } from "./ui/modal";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { UploadCloud, XCircle } from "lucide-react";
import { uploadMovementDocument } from "../lib/api";
import { getBackendErrorMessage } from "../lib/utils";
import { toast } from "sonner";

interface UploadDocumentModalProps {
  open: boolean;
  onClose: () => void;
  movementId?: string;
  onUpload?: (file: File) => Promise<unknown>;
  onSuccess?: () => void;
}

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
  open,
  onClose,
  movementId,
  onUpload,
  onSuccess,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (file: File) => {
    setUploadedFile(file);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    if (!uploadedFile || (!onUpload && !movementId)) return;

    try {
      setIsUploading(true);
      if (onUpload) {
        await onUpload(uploadedFile);
      } else {
        await uploadMovementDocument(movementId!, uploadedFile);
      }

      // Reset form
      setUploadedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }

      // Close modal
      onClose();
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to upload document"));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      className="max-w-2xl"
      title="Upload Document"
    >
      <div className="space-y-6 p-6">
        <div className="space-y-2">
          <Label htmlFor="upload document" className="font-medium">
            Choose Document to Upload
          </Label>
          <div className="space-y-4">
            <div
              className={`border border-dashed rounded-lg p-8 flex justify-center items-center 
        ${dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300"}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                id="upload-input"
                type="file"
                className="hidden"
                onChange={handleChange}
              />

              <div className="flex items-center space-x-2">
                <Button
                  variant="outlinePrimary"
                  type="button"
                  onClick={handleButtonClick}
                >
                  <UploadCloud className="w-5 h-5 mr-2" />
                  Upload Document
                </Button>
                <span className="text-gray-400">or drag and drop here</span>
              </div>
            </div>

            {uploadedFile && (
              <div className="flex justify-between items-center border rounded-lg p-3 bg-gray-50">
                <span className="text-gray-700">{uploadedFile.name}</span>
                <Button
                  variant="ghost"
                  onClick={handleRemoveFile}
                  className="text-red-500 hover:text-red-700"
                >
                  <XCircle className="w-5 h-5" />
                  <span>Remove</span>
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="outlinePrimary"
            onClick={onClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            disabled={!uploadedFile || isUploading}
            onClick={handleSave}
          >
            {isUploading ? "Uploading..." : "Save"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UploadDocumentModal;
